import React from 'react';
import Icon from '@mdi/react';
import {
    mdiCheck,
    mdiThemeLightDark,
    mdiWeatherNight,
    mdiWhiteBalanceSunny,
} from '@mdi/js';
import styles from './ThemeToggle.module.css';
import { ThemePreference } from '@/lib/ThemeContext';
import { useTheme } from '@/lib/useTheme';

const options: Array<{
    value: ThemePreference;
    label: string;
    description: string;
    icon: string;
}> = [
        {
            value: 'system',
            label: 'Sistema',
            description: 'Segue o tema do dispositivo',
            icon: mdiThemeLightDark,
        },
        {
            value: 'light',
            label: 'Claro',
            description: 'Prioriza leitura em superfícies claras',
            icon: mdiWhiteBalanceSunny,
        },
        {
            value: 'dark',
            label: 'Escuro',
            description: 'Realça dados e reduz brilho do chrome',
            icon: mdiWeatherNight,
        },
    ];

const ThemeToggle = () => {
    const { themePreference, setThemePreference, resolvedTheme } = useTheme();
    const [isOpen, setIsOpen] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (!isOpen) return;

        const handlePointerDown = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    const activeOption = options.find((option) => option.value === themePreference) ?? options[0];
    const resolvedIcon = resolvedTheme === 'dark' ? mdiWeatherNight : mdiWhiteBalanceSunny;

    return (
        <div className={styles.container} ref={containerRef}>
            <button
                type="button"
                className={styles.trigger}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label={`Tema: ${activeOption.label}`}
                title={`Tema: ${activeOption.label}`}
                onClick={() => setIsOpen((current) => !current)}
            >
                <span className={styles.triggerIcon}>
                    <Icon path={resolvedIcon} size={0.9} />
                </span>
            </button>

            {isOpen ? (
                <div className={styles.menu} role="menu" aria-label="Selecionar tema">
                    {options.map((option) => {
                        const isSelected = option.value === themePreference;

                        return (
                            <button
                                key={option.value}
                                type="button"
                                role="menuitemradio"
                                aria-checked={isSelected}
                                className={[styles.option, isSelected ? styles.optionSelected : ''].join(' ')}
                                onClick={() => {
                                    setThemePreference(option.value);
                                    setIsOpen(false);
                                }}
                            >
                                <span className={styles.optionIcon}>
                                    <Icon path={option.icon} size={0.88} />
                                </span>
                                <span className={styles.optionText}>
                                    <span className={styles.optionLabel}>{option.label}</span>
                                    <span className={styles.optionDescription}>{option.description}</span>
                                </span>
                                <span className={styles.optionCheck}>
                                    {isSelected ? <Icon path={mdiCheck} size={0.75} /> : null}
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

export default ThemeToggle;